import api from "../utils/axios"

export const caApi = {
    // CA: Client Endpoints
    getAllClients: () => api.get('/users/ca/clients/'),
    retrieveClientDetails: (clientId) => api.get(`/users/ca/clients/${clientId}`),
    createClient: (data) => api.post('/users/ca/clients/create/', data),
    updateClient: async (clientId, data) => api.put(`users/ca/clients/${clientId}/`, data),
    getCAClients: (search) => api.get(`/users/ca/clients/search/?q=${search}`),
    getCAClientsBusiness: (search) => api.get(`/users/ca/business/search/?q=${search}`),

    // CA: Client - Document Request Endpoints
    getAllClientDocumentRequests: () => api.get('/client_requests/ca/document-requests/'),
    getClientDocumentRequests: (clientId) => api.get(`/client_requests/ca/clients/${clientId}/document-requests/`),
    retrieveClientDocumentRequest: (clientId, docRequestId) => api.get(`/client_requests/ca/clients/${clientId}/document-requests/${docRequestId}/`),
    createClientDocumentRequest: (clientId, data) => api.post(`/client_requests/ca/clients/${clientId}/document-requests/`, data),
    updateClientDocumentRequest: (clientId, docRequestId, data) => api.patch(`/client_requests/ca/clients/${clientId}/document-requests/${docRequestId}/`, data),
    destroyClientDocumentRequest: (clientId, docRequestId) => api.delete(`/client_requests/ca/clients/${clientId}/document-requests/${docRequestId}/`),

    // CA: Client - Business Endpoints
    getClientBusiness: (clientId) => api.get(`/users/ca/clients/${clientId}/businesses/`),
    retrieveClientBusiness: (clientId, businessId) => api.get(`/users/ca/clients/${clientId}/businesses/${businessId}/`),
    createClientBusiness: (clientId, data) => api.post(`/users/ca/clients/${clientId}/businesses/`, data),
    updateClientBusiness: (clientId, businessId, data) => api.patch/(`users/ca/clients/${clientId}/businesses/${businessId}/`, data),
    destroyClientBusiness: (clientId, businessId) => api.delete(`/users/ca/clients/${clientId}/businesses/${businessId}/`),

    // CA: Client - Invoice Endpoints
    listClientInvoices : () => api.get('/documents/ca/clients/invoices/'),
    retrieveClientInvoice : (invoiceId) => api.get(`/documents/ca/clients/invoices/${invoiceId}/`),
    updateClientInvoice : (invoiceId, data) => api.put(`/documents/ca/clients/invoices/${invoiceId}/update/`, data),
    destroyClientInvoice : (invoiceId) => api.delete(`/documents/ca/clients/invoices/${invoiceId}/delete/`),
    approveClientInvoice : (invoiceId) => api.post(`/documents/ca/clients/invoices/${invoiceId}/approve/`),
    bulkApproveClientInvoice : (businessId) => api.post(`/documents/ca/businesses/${businessId}/invoices/approve-all/`),

    // CA: Client - Invoice File Upload Endpoints
    createClientInvoice: async (businessId, file) => {
        if (!businessId) { throw new Error("Business is required"); }
        if (!file) { throw new Error("Invoice file is required"); }

        const formData = new FormData();
        formData.append("business", businessId);
        formData.append("file", file);
        return await api.post(
            "/documents/ca/clients/invoices/create/",
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
    },

    // CA: Client - Generate Invoice Excel File Endpoints
    generateBusinessInvoiceExcelFile: async (businessId) => {
        if (!businessId) { throw new Error("Business is required"); }
        const res = await api.get(
            `/reports/ca/generate/excel/?business=${businessId}`,
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "invoices_excel.xlsx"); // filename
        document.body.appendChild(link);
        link.click();
        link.remove();
    },

    // CA: Client - Upload Invoice Excel File Endpoints
    uploadClientExcel: async (businessId, file) => {
        if (!businessId) { throw new Error("Business is required"); }
        if (!file) { throw new Error("Invoice file is required"); }

        const formData = new FormData();
        formData.append("business", businessId);
        formData.append("file", file);
        const res = await api.post(
            "/reports/ca/import/excel/", 
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        return res.data;
    },

    // CA: Accounting
    businessLedgers: (businessId) => api.get(`/accounting/ca/${businessId}/ledgers/`),
    businessLedgersDetails: (businessId, ledgerId) => api.get(`/accounting/ca/${businessId}/ledgers/${ledgerId}/`),
    businessAccountGroups: (businessId) => api.get(`/accounting/ca/${businessId}/account-groups/`),
    businessAccountGroupsDetails: (businessId, accountGroupId) => api.get(`/accounting/ca/${businessId}/account-groups/${accountGroupId}/`),

    // CA: Client Payment Request Overview
    getPaymentOverview: () => api.get('/payments/ca/payment-overview/'),
    getPlatformOverview: () => api.get('/payments/ca/platform-overview/'),
    
    // CA: Client Payment Request
    getAllPaymentRequests: () => api.get('/payments/ca/requests/'),
    createPaymentRequest: (data) => api.post("/payments/ca/requests/", data),
    updatePaymentRequest: (id, data) => api.patch(`/payments/ca/requests/${id}/`, data),
    createPaymentRequestDraft: (data) => api.post("/payments/ca/requests/draft/", data),
    updatePaymentRequestDraft: (id, data) => api.patch(`/payments/ca/requests/draft/${id}/`, data),
    retrievePaymentRequest: (id) => api.get(`/payments/ca/requests/${id}`),
    deletePaymentRequest: (id) => api.delete(`/payments/ca/requests/${id}/cancel/`),

    // "/payments/ca/reminders/"
    // "/payments/ca/reminders/<int:pk>/"
    // "/payments/ca/reminders/<int:pk>/pause/"
    // "/payments/ca/reminders/<int:pk>/resume/"
    // "/payments/ca/reminder-logs/"
    // "/payments/ca/reminder-logs/<int:pk>/"

    // DASHBOARD
    getDashboard: () => api.get("/ca_firms/dashboard/"),
    getEmployeeDashboard: () => api.get("/ca_firms/employee/dashboard/"),

    // FIRMS
    getFirms: () => api.get("/ca_firms/firms/"),
    createFirm: (data) => api.post("/ca_firms/firms/", data),
    getFirm: (firmId) => api.get(`/ca_firms/firms/${firmId}/`),
    updateFirm: (firmId, data) => api.patch(`/ca_firms/firms/${firmId}/`, data),
    deleteFirm: (firmId) => api.delete(`/ca_firms/firms/${firmId}/delete/`),

    // EMPLOYEES
    getEmployees: () => api.get("/ca_firms/employees/"),
    getEmployeeCreateOptions: () => api.get("/ca_firms/employees/options/"),
    createEmployee: (data) => api.post("/ca_firms/employees/", data),
    getEmployee: (employeeId) => api.get(`/ca_firms/employees/${employeeId}/`),
    updateEmployee: (employeeId, data) => api.patch(`/ca_firms/employees/${employeeId}/`, data),

    activateEmployee: (employeeId) => api.post(`/ca_firms/employees/${employeeId}/activate/`),
    deactivateEmployee: (employeeId) => api.post(`/ca_firms/employees/${employeeId}/deactivate/`),

    // EMPLOYEE ROLES
    assignEmployeeRole: (employeeId, data) => api.post(`/ca_firms/employees/${employeeId}/roles/`, data),
    removeEmployeeRole: (employeeId, data) => api.post(`/ca_firms/employees/${employeeId}/roles/remove/`, data),

    // TASKS
    getTasks: (params = {}) => api.get("/ca_firms/tasks/", { params }),
    getTaskCreateOptions: () => api.get("/ca_firms/tasks/create-options/"),
    createTask: (data) => api.post("/ca_firms/tasks/", data),
    getTask: (taskId) => api.get(`/ca_firms/tasks/${taskId}/`),
    updateTask: (taskId, data) => api.patch(`/ca_firms/tasks/${taskId}/`, data),
    deleteTask: (taskId) => api.delete(`/ca_firms/tasks/${taskId}/delete/`),

    // TASK WORKFLOW
    startTask: (taskId, employeeId = null) =>
        api.post(
            `/ca_firms/tasks/${taskId}/workflow/`,
            {
                action: "start",
                ...(employeeId
                    ? { employee_id: employeeId }
                    : {})
            }
        ),

    pauseTask: (taskId, employeeId) =>
        api.post(
            `/ca_firms/tasks/${taskId}/workflow/`,
            {
                action: "pause",
                ...(employeeId
                    ? { employee_id: employeeId }
                    : {})
            }
        ),

    resumeTask: (taskId, employeeId = null) =>
        api.post(
            `/ca_firms/tasks/${taskId}/workflow/`,
            {
                action: "resume",
                ...(employeeId
                    ? { employee_id: employeeId }
                    : {}),
            }
        ),

    completeTask: (taskId, employeeId = null) =>
        api.post(
            `/ca_firms/tasks/${taskId}/workflow/`,
            {
                action: "complete",
                ...(employeeId
                    ? { employee_id: employeeId }
                    : {}),
            }
        ),

    cancelTask: (taskId) =>
        api.post(
            `/ca_firms/tasks/${taskId}/workflow/`,
            { action: "cancel" }
        ),

    // Backward-compatible generic workflow method.
    workflowTask: (taskId, data) => api.post(`/ca_firms/tasks/${taskId}/workflow/`, data),

    // Task Details
    getTaskActivities: (taskId) => api.get(`/ca_firms/tasks/${taskId}/activities/`),
    getTaskNotes: (taskId) => api.get(`/ca_firms/tasks/${taskId}/notes/`),

    //Team Details
    getTeamCalendar: () => api.get("/ca_firms/team/calendar/"),

    // OFFICES
    getOffices: () => api.get("/ca_firms/offices/"),
    createOffice: (payload) => api.post("/ca_firms/offices/", payload),
    getOffice: (officeId) => api.get(`/ca_firms/offices/${officeId}/`),
    updateOffice: (officeId, payload) => api.patch(`/ca_firms/offices/${officeId}/`, payload),
    deactivateOffice: (officeId) => api.post(`/ca_firms/offices/${officeId}/deactivate/`),
    activateOffice: (officeId) => api.post(`/ca_firms/offices/${officeId}/activate/`),
    getOfficeCreateOptions: () => api.get("ca_firms/offices/form-options/"),

    // DEPARTMENTS
    getDepartments: () => api.get("/ca_firms/departments/"),
    getDepartment: (departmentId) => api.get(`/ca_firms/departments/${departmentId}/`),
    createDepartment: (payload) => api.post("/ca_firms/departments/", payload),
    updateDepartment: (departmentId, payload) => api.patch(`/ca_firms/departments/${departmentId}/`, payload),
    deleteDepartment: (departmentId) => api.delete(`/ca_firms/departments/${departmentId}/delete/`),

    // TEAMS
    getTeams: () => api.get("/ca_firms/teams/"),
    getTeam: (teamId) => api.get(`/ca_firms/teams/${teamId}/`),
    createTeam: (payload) => api.post("/ca_firms/teams/", payload),
    updateTeam: (teamId, payload) => api.patch(`/ca_firms/teams/${teamId}/`, payload),
    // deleteTeam: (teamId) => api.delete(`/ca_firms/teams/${teamId}/delete/`),
    deactivateTeam: (teamId) => api.post(`/ca_firms/teams/${teamId}/deactivate/`),
    activateTeam: (teamId) => api.post(`/ca_firms/teams/${teamId}/activate/`),
    getTeamCreateOptions: () => api.get("/ca_firms/teams/options/"),
};
